import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { isEqual } from 'lodash';
import { Formik } from 'formik';
import * as Yup from 'yup';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import {
  showSuccess,
  updateSingleTemplate,
  updateTemplateData
} from 'store/slices';

import Button from 'components/form/Button';
import Label from 'components/form/Label';
import TextInput from 'components/form/TextInput';
import ToggleSwitch from 'components/form/ToggleSwitch';
import Card from 'components/Layout/Card';
import { icons } from 'utils/constants';
import { objectToFormData } from 'utils/helpers';
import usePermission from 'hooks/usePermission';

const EmailTemplateForm = ({ template, setSelectedTemplate, onClose, onHistory }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { viewAccess, updateAccess } = usePermission("Email Templates");
  const fieldIds = { status: 1, subject: 2, body: 3 };

  if (!template) return null;

  const initialValues = {
    template_id: template.id || '',
    trigger_flow: template.trigger_on || '',
    status: template.status ?? 0,
    subject: template.subject || '',
    body: template.body || '',
    edited_details: '',
  };

  const validationSchema = Yup.object().shape({
    trigger_flow: Yup.string(),
    status: Yup.number()
      .oneOf([0, 1], 'Template status must be either Active or Inactive')
      .required('Template status is required.'),
    subject: Yup.string()
      .required('Email subject is required.')
      .max(150, 'Email subject cannot exceed 150 characters.'),
    body: Yup.string().required('Email body is required.'),
  });

  const handleSave = async (values) => {
    try {
      setLoading(true);

      const changedFields = [];
      if (values.status !== initialValues.status) changedFields.push(fieldIds.status);
      if (values.subject !== initialValues.subject) changedFields.push(fieldIds.subject);
      if (values.body !== initialValues.body) changedFields.push(fieldIds.body);

      const payload = { ...values, edited_details: changedFields.join(',') };
      const res = await dispatch(updateTemplateData(objectToFormData(payload)));

      if (res?.data) {
        setSelectedTemplate(res.data);
        dispatch(updateSingleTemplate(res.data));
        onClose();
        dispatch(showSuccess(res.message || 'Template updated successfully!'));
      }
    } finally {
      setLoading(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'], //'video'
      [{ align: [] }],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'link', 'image',  'align', //'video',
  ];

  return (
    <Card className="cp-20 h-100">
      <div className="row cmt-49">
        <div className="col-12">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSave}
          >
            {({ values, handleChange, setFieldValue, errors, resetForm, handleSubmit }) => (
              <div className="form row">

                {/* Trigger Flow */}
                <div className="col-md-8 cmb-20">
                  <TextInput
                    label="Trigger Flow :"
                    labelClass="cmb-14 color-text-blue"
                    id="trigger_flow"
                    value={values.trigger_flow || '-'}
                    onChange={handleChange}
                    error={errors.trigger_flow}
                    disabled
                  />
                </div>

                {/* Template Status */}
                <div className="col-md-4 cmb-20">
                  <Label label="Template Status" className="cmb-14 color-text-blue" />
                  <div className="d-flex align-items-center">
                    <ToggleSwitch
                      isChecked={values.status === 1}
                      onChange={(e) => setFieldValue('status', e.target.checked ? 1 : 0)}
                    />
                  </div>
                  {errors.status && (
                    <div className="text-13-400 pt-1" style={{ color: 'red' }}>
                      {errors.status}
                    </div>
                  )}
                </div>

                {/* Subject */}
                <div className="col-md-12 cmb-20">
                  <TextInput
                    label="Email Subject :"
                    labelClass="cmb-14 color-text-blue"
                    id="subject"
                    value={values.subject}
                    onChange={handleChange}
                    error={errors.subject}
                  />
                </div>

                {/* Email Body */}
                <div className="col-md-12 cmb-20">
                  <Label label="Email Body :" className="cmb-14 color-text-blue" />
                  <ReactQuill
                    theme="snow"
                    value={values.body}
                    onChange={(content) => setFieldValue('body', content)}
                    modules={quillModules}
                    formats={quillFormats}
                  />
                  {errors.body && (
                    <div className="text-13-400 pt-1" style={{ color: 'red' }}>
                      {errors.body}
                    </div>
                  )}
                </div>


                {/* Action Buttons */}
                <div className="cmt-20 d-flex gap-3">
                  {/* Save & Reset */}
                  {updateAccess === "1" && (
                    <>
                      <Button
                        icon={
                          <span className="me-2">
                            <img
                              src={icons.NewTickCircle}
                              alt=""
                              height={18}
                              width={18}
                              style={{ filter: 'brightness(0) saturate(100%) invert(100%)' }}
                            />
                          </span>
                        }
                        btnStyle="primary-dark"
                        className="cps-15 cpe-15 h-37"
                        text={loading ? 'Saving...' : 'Save Changes'}
                        onClick={handleSubmit}
                        disabled={isEqual(initialValues, values) || loading}
                        btnLoading={loading}
                      />

                      <Button
                        icon={
                          <span className="me-2">
                            <img src={icons.RotateRight} alt="" height={18} width={18} />
                          </span>
                        }
                        btnStyle="primary-outline-car"
                        className="cps-15 cpe-15 h-37"
                        text="Reset"
                        onClick={resetForm}
                        disabled={loading}
                      />
                    </>
                  )}

                  {/* History */}
                  {viewAccess === "1" && (
                    <Button
                      icon={
                        <span className="me-2">
                          <img src={icons.historyDarkIcon} alt="" height={18} width={18} />
                        </span>
                      }
                      btnStyle="light-outline bg-f5f5ff"
                      className="cps-15 cpe-15 h-37"
                      text="History"
                      onClick={onHistory}
                      disabled={loading}
                    />
                  )}
                </div>

              </div>
            )}
          </Formik>
        </div>
      </div>
    </Card>
  );
};

export default EmailTemplateForm;
